'use client';

/**
 * useConversationPreviews — session preview cache for history rows.
 *
 * Client-side interim stand-in for the backend `preview` list field: derives
 * a live last-message snippet from the in-memory transcript, remembers it in
 * the conversation store's previewCache (keyed by conversation id), and
 * returns the whole cache for merge into the fetched list (see
 * utils.applyPreviewCache — a server-provided preview always wins).
 *
 * The cache is what keeps a row's preview line from vanishing while a
 * conversation is loading: resume clears the transcript, but the last-known
 * snippet for every conversation visited this session stays available.
 */

import { useEffect, useMemo } from 'react';
import { useConversationStore } from '@/stores/conversation-store';
import { MessageRole, type Message } from '@/types/chat';

// Assistant replies are Markdown; a preview is plain text. Mirrors the
// backend's _build_preview rules exactly (aol-us-ai-asst db/repository.py) —
// keep the two rule sets in sync. Conservative on purpose: single `_` stays
// (snake_case), only line-leading heading/quote/list markers are removed.
const MD_LINK_RE = /!?\[([^\]]*)\]\([^)]*\)/g;
const MD_LINE_PREFIX_RE = /^\s{0,3}(#{1,6}\s+|>\s?|[-+*]\s+|\d+\.\s+)/gm;
const MD_MARKER_RE = /(\*{1,3}|__|`{1,3}|~~)/g;

/** Live last-message snippet from the in-memory transcript. Mirrors the
 *  backend preview rules: latest USER/ASSISTANT text, Markdown stripped,
 *  whitespace flattened, ~140 chars. */
export function livePreviewFromMessages(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (
      (m.role === MessageRole.USER || m.role === MessageRole.ASSISTANT) &&
      typeof m.content === 'string' &&
      m.content.trim()
    ) {
      return m.content
        .replace(MD_LINK_RE, '$1')
        .replace(MD_LINE_PREFIX_RE, '')
        .replace(MD_MARKER_RE, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 140);
    }
  }
  return null;
}

/**
 * Keeps the store's previewCache fed from the active transcript and returns
 * it. Call with the ChatContext `messages` array.
 */
export function useConversationPreviews(
  messages: Message[]
): Record<string, string> {
  const conversationId = useConversationStore((s) => s.conversationId);
  const expectingHistory = useConversationStore((s) => s.expectingHistory);
  const freshChatIntent = useConversationStore((s) => s.freshChatIntent);
  const previewCache = useConversationStore((s) => s.previewCache);
  const cachePreview = useConversationStore((s) => s.cachePreview);

  const live = useMemo(() => livePreviewFromMessages(messages), [messages]);

  useEffect(() => {
    // Guard the transition window right after a resume/new-chat: the store
    // already carries the NEW conversationId while `messages` still holds the
    // OLD conversation's transcript (it clears when the reconnect lands).
    // Caching during that window would file the old snippet under the new id.
    if (expectingHistory || freshChatIntent) return;
    if (conversationId && live) cachePreview(conversationId, live);
  }, [conversationId, live, expectingHistory, freshChatIntent, cachePreview]);

  return previewCache;
}
